import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAuth,
  useUserReservationStateStream,
  type UserReservationStateEvent,
} from "@bw/shared/hooks";

import { queryKeys } from "../../../common/query/queryClient";

/** Keep reservation-backed player screens canonical from one user-scoped stream. */
export function PlayerReservationStateSync() {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const reconcile = useCallback(
    async (events: readonly UserReservationStateEvent[] | undefined) => {
      const facilityIds = events
        ? [...new Set(events.map((event) => event.facilityId))]
        : [];
      const invalidate = (queryKey: readonly unknown[]) =>
        queryClient.invalidateQueries({ queryKey }, { throwOnError: true });

      const critical = [
        invalidate(queryKeys.booking.allBookings()),
        invalidate(queryKeys.play.myUpcoming()),
        invalidate(queryKeys.booking.detail("").slice(0, 2)),
        invalidate(queryKeys.booking.participants("", "").slice(0, 2)),
        invalidate(queryKeys.booking.invitations("", "").slice(0, 2)),
        invalidate(queryKeys.booking.myInvitations()),
      ];
      if (facilityIds.length === 0) {
        critical.push(
          invalidate(["booking", "my-bookings"]),
          invalidate(["booking", "availability"]),
        );
      } else {
        for (const facilityId of facilityIds) {
          critical.push(
            invalidate(queryKeys.booking.myBookings(facilityId)),
            invalidate(
              queryKeys.booking.availability(facilityId, "").slice(0, 3),
            ),
          );
        }
      }
      await Promise.all(critical);

      // Metadata is useful to an open booking sheet but must not make a failed
      // optional read retry every status/list query at a busy facility.
      const optional = facilityIds.flatMap((facilityId) => [
        queryClient.invalidateQueries({
          queryKey: queryKeys.booking.courts(facilityId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.booking.operatingHours(facilityId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.booking.pricing(facilityId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.booking.facility(facilityId),
        }),
      ]);
      void Promise.allSettled(optional);
    },
    [queryClient],
  );

  useUserReservationStateStream({
    onReconcile: reconcile,
    identityKey: user?.uid ?? null,
    enabled: !loading && user !== null,
  });
  return null;
}
